import React from "react";
import { ConfigProvider, Radio, Input as AntdInput, Button } from "antd";
import { PhoneOutlined, DeleteOutlined, PlusOutlined, CheckCircleFilled } from "@ant-design/icons";
import trans from "../../../utils";

const BRAND_GREEN = "#0f6936";

// Shared "Contact Number" panel: a Primary Number box plus a unified
// Additional Phone list (landline + secondary phones). `matchedNumber` is the
// currently-selected/matched number, highlighted wherever it equals a row's
// (or the primary's) value. Purely presentational — no Formik dependency —
// so it works unchanged in the read-only View Request popup as well as the
// create/edit forms.
//
// Two independent modes:
//  - `editable`: the landline/secondary text fields themselves can be typed
//    into, removed, or added to.
//  - `selectable`: the radio indicators become clickable, letting the agent
//    pick which saved number this specific call actually came from — it
//    doesn't have to be the one auto-matched by the phone lookup.
export default function ContactNumberList({
  primaryNumber = undefined, // omit entirely when the caller already shows the primary number elsewhere
  matchedNumber,
  rows = [],
  editable = false,
  selectable = false,
  onChangeRow,
  onRemoveRow,
  onAddRow,
  onSelect,
  canAddMore = false,
  errorsByKey = {},
  touchedByKey = {},
}) {
  const showPrimary = primaryNumber !== undefined;
  const primaryMatched = !!matchedNumber && !!primaryNumber && matchedNumber === primaryNumber;
  const visibleRows = editable ? rows : rows.filter((row) => !!row.value);
  const rowPlaceholder = (kind) =>
    kind === "landline" ? trans("Home Telephone") : trans("Additional Phone");

  return (
    <div className="contact-number-list">
      {showPrimary && (
        <div className="mb-3">
          <label className="form-label d-block">{trans("Primary Number")}</label>
          <ConfigProvider theme={{ token: { colorPrimary: BRAND_GREEN } }}>
            <div className={`primary-number-box${primaryMatched ? " matched" : ""}`}>
              {selectable ? (
                <Radio
                  checked={primaryMatched}
                  disabled={!primaryNumber}
                  onChange={() => onSelect?.(primaryNumber)}
                />
              ) : (
                <PhoneOutlined className="text-muted" />
              )}
              <span>{primaryNumber || "-"}</span>
              {!selectable && primaryMatched && (
                <CheckCircleFilled
                  className="primary-number-matched-icon"
                  style={{ color: BRAND_GREEN }}
                  title={trans("Phone number used for this call")}
                />
              )}
            </div>
          </ConfigProvider>
        </div>
      )}
      <div>
        <label className="form-label d-block">{trans("Additional Phone")}</label>
        <ConfigProvider theme={{ token: { colorPrimary: BRAND_GREEN } }}>
          <div className="phone-list">
            {visibleRows.length === 0 && !editable && <span className="text-muted">-</span>}
            {visibleRows.map((row) => {
              const isMatched = !!matchedNumber && !!row.value && row.value === matchedNumber;
              const itemError = errorsByKey[row.key];
              const itemTouched = touchedByKey[row.key];
              const showItemError = itemError && (row.value ? true : itemTouched);
              return (
                <div key={row.key} className={`phone-list-row mb-2${isMatched ? " matched" : ""}`}>
                  <Radio
                    checked={isMatched}
                    disabled={!selectable || !row.value}
                    onChange={() => onSelect?.(row.value)}
                  />
                  {editable ? (
                    <AntdInput
                      type="text"
                      placeholder={rowPlaceholder(row.kind)}
                      value={row.value}
                      onChange={(e) => onChangeRow?.(row.key, e.target.value)}
                      addonBefore={<PhoneOutlined />}
                      status={showItemError ? "error" : undefined}
                      suffix={
                        <DeleteOutlined
                          className="text-danger cursor-pointer"
                          onClick={() => onRemoveRow?.(row.key)}
                        />
                      }
                    />
                  ) : (
                    <span className="phone-list-row-value">{row.value}</span>
                  )}
                  {showItemError && (
                    <div className="text-danger" style={{ fontSize: "12px", marginTop: "4px" }}>
                      {itemError}
                    </div>
                  )}
                </div>
              );
            })}
            {editable && canAddMore && (
              <Button type="link" size="small" icon={<PlusOutlined />} onClick={onAddRow} className="p-0">
                {trans("Add Another Number")}
              </Button>
            )}
          </div>
        </ConfigProvider>
      </div>
    </div>
  );
}
